// OmniAuth appends its own /auth/:provider suffix to the Devise path.
// Our Devise path is /api/v1/auth, so the full authorize URL is:
//   /api/v1/auth/auth/:provider   ← the double "auth" is intentional
const API_BASE   = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1') as string
const OAUTH_BASE = `${API_BASE}/auth/auth`

/** Build the URL that triggers OmniAuth's OAuth redirect for a given provider. */
function oauthUrl(provider: 'google_oauth2' | 'facebook' | 'apple'): string {
  return `${OAUTH_BASE}/${provider}`
}

// ─── Provider icons (inline SVG — no extra dependency) ────────────────────────

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        fill="#EA4335"
      />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="white">
      <path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="white">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.39.07 2.35.74 3.15.8 1.19-.24 2.35-.93 3.62-.84 1.54.12 2.69.73 3.45 1.84-3.17 1.9-2.42 5.97.48 7.13-.57 1.53-1.32 3.04-2.7 3.95M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25" />
    </svg>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  /** "Sign in" (login page) or "Sign up" (register page) */
  action?: 'sign_in' | 'sign_up'
}

export default function SocialLoginButtons({ action = 'sign_in' }: Props) {
  const label = action === 'sign_up' ? 'Sign up' : 'Continue'

  return (
    <div className="space-y-3">
      <a
        href={oauthUrl('google_oauth2')}
        className="flex items-center justify-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
      >
        <GoogleIcon />
        {label} with Google
      </a>

      <a
        href={oauthUrl('facebook')}
        className="flex items-center justify-center gap-3 w-full px-4 py-3 rounded-xl border border-transparent bg-[#1877F2] hover:bg-[#166FE5] text-sm font-medium text-white transition-colors shadow-sm"
      >
        <FacebookIcon />
        {label} with Facebook
      </a>

      <a
        href={oauthUrl('apple')}
        className="flex items-center justify-center gap-3 w-full px-4 py-3 rounded-xl border border-transparent bg-black hover:bg-gray-900 text-sm font-medium text-white transition-colors shadow-sm"
      >
        <AppleIcon />
        {label} with Apple
      </a>
    </div>
  )
}
